import { useState, useEffect } from 'react';
import { format, setHours, setMinutes, startOfMonth } from 'date-fns';
import type { DateRange } from '@daypicker/react';
import clsx from 'clsx';

import { DayPicker } from './DayPicker';
import { TimePicker } from './TimePicker';
import Button from '../Button';
import {
  type DateRangeValue,
  type Preset,
  getPresets,
  isActivePreset,
  valueToDateRange,
  dateRangeToValue,
} from './utils';

const applyTimeToDate = (date: Date, time: string) => {
  const [hours, minutes] = time.split(':').map(Number);
  return setMinutes(setHours(date, hours), minutes);
};

const isSameCalendarDay = (
  a: Date | undefined,
  b: Date | undefined
): boolean => {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
};

const END_TIME = '23:59';
const START_TIME = '00:00';

const normalizeRangeSelection = (
  range: DateRange | undefined,
  prev: DateRange | undefined
): DateRange | undefined => {
  if (!range?.from && !range?.to) return range;

  const from = range.from
    ? isSameCalendarDay(range.from, prev?.from) && prev?.from
      ? applyTimeToDate(range.from, format(prev.from, 'HH:mm'))
      : applyTimeToDate(range.from, START_TIME)
    : undefined;

  const to = range.to
    ? isSameCalendarDay(range.to, prev?.to) && prev?.to
      ? applyTimeToDate(range.to, format(prev.to, 'HH:mm'))
      : applyTimeToDate(range.to, END_TIME)
    : undefined;

  return { from, to };
};

type RangeDayPickerProps = {
  value?: DateRangeValue;
  onChange?: (range: DateRangeValue) => void;
  onCancel?: () => void;
};

export const RangeDayPicker: React.FC<RangeDayPickerProps> = ({
  value,
  onChange,
  onCancel,
}) => {
  const [internalRange, setInternalRange] = useState<DateRange | undefined>(
    valueToDateRange(value)
  );
  const [currentMonth, setCurrentMonth] = useState<Date | undefined>(
    value?.[0] ? startOfMonth(value[0]) : undefined
  );

  const fromTime = value?.[0]?.getTime();
  const toTime = value?.[1]?.getTime();

  useEffect(() => {
    setInternalRange(valueToDateRange(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromTime, toTime]);

  const from = internalRange?.from;
  const to = internalRange?.to;
  const presets = getPresets();

  const handlePreset = (preset: Preset) => {
    const [pFrom, pTo] = preset.getRange();
    const newRange =
      pFrom || pTo
        ? normalizeRangeSelection({ from: pFrom, to: pTo }, internalRange)
        : undefined;
    setInternalRange(newRange);
    const navigateTo = pFrom ?? pTo;
    if (navigateTo) {
      setCurrentMonth(startOfMonth(navigateTo));
    }
  };

  const handleRangeSelect = (range: DateRange | undefined) => {
    setInternalRange(prev => normalizeRangeSelection(range, prev));
  };

  const handleOutsideDayClick = (
    date: Date,
    modifiers: Record<string, boolean>
  ) => {
    if (modifiers.outside) {
      (document.activeElement as HTMLElement)?.blur();
      setTimeout(() => setCurrentMonth(startOfMonth(date)), 0);
    }
  };

  const handleApply = () => {
    onChange?.(dateRangeToValue(internalRange));
  };

  const handleCancel = () => {
    setInternalRange(valueToDateRange(value));
    onCancel?.();
  };

  const handleFromChange = (date: Date) => {
    setInternalRange(prev => (prev ? { ...prev, from: date } : prev));
  };

  const handleToChange = (date: Date) => {
    setInternalRange(prev => (prev ? { ...prev, to: date } : prev));
  };

  return (
    <div className="sp-range-daypicker flex bg-content border border-active rounded-md shadow-lg">
      <div className="flex flex-col gap-xs px-md py-md border-r border-active min-w-[16rem]">
        {presets.map(preset => (
          <Button
            key={preset.label}
            type="ghost"
            fullWidth
            justify="start"
            label={preset.label}
            onClick={() => handlePreset(preset)}
            className={clsx(
              'font-normal text-text',
              isActivePreset(preset, from, to) &&
                'bg-[var(--dp-preset-active-bg)] hover:bg-[var(--dp-preset-active-bg)]'
            )}
          />
        ))}
      </div>

      <div className="flex flex-col">
        <DayPicker
          mode="range"
          classNames={{
            months: 'flex-nowrap gap-0',
            month: 'pb-lg px-md first:border-r border-active',
            month_caption: 'pt-xl',
          }}
          numberOfMonths={2}
          month={currentMonth}
          selected={internalRange}
          onSelect={handleRangeSelect}
          onMonthChange={setCurrentMonth}
          onDayClick={handleOutsideDayClick}
        />

        <div className="flex items-center justify-between px-md py-md border-t border-active gap-lg">
          <div className="flex items-center gap-md">
            <TimePicker
              label="From:"
              value={from}
              disabled={!from}
              onChange={handleFromChange}
            />
            <span className="text-medium text-textLight">-</span>
            <TimePicker
              label="To:"
              value={to}
              disabled={!to}
              onChange={handleToChange}
            />
          </div>

          <div className="flex items-center gap-md">
            <Button
              type="light"
              label="Cancel"
              className="px-lg"
              onClick={handleCancel}
            />
            <Button
              type="main"
              label="Apply"
              className="px-lg"
              onClick={handleApply}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
